// MainLayout.js - Three-Panel Layout

import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useReactFlow,
  addEdge,
  applyEdgeChanges,
  MarkerType
} from '@xyflow/react';
import useStore, { temporalStore } from '../../store/useStore';
import { tutorialCompletedKey } from '../../store/slices/tutorialSlice';
import THEME from '../../styles/theme';
import Header from './Header';
import LeftPanel from './LeftPanel';
import RightPanel from './RightPanel';
import FloatingInspector from './FloatingInspector';
import PlotPanel from '../Plots/PlotPanel';
import ComponentDetailsPanel from '../Panels/ComponentDetailsPanel';
import ComponentContextMenu from '../Menus/ComponentContextMenu';
import EdgeContextMenu from '../Menus/EdgeContextMenu';
import NodeContextMenu from '../Menus/NodeContextMenu';
import RFNode from '../Nodes/RFNode';
import NoteNode from '../Nodes/NoteNode';
import MeasurementNode from '../Nodes/MeasurementNode';
// import EditModal from '../Modals/EditModal'; // Removed - using Component Details Panel instead
import NoteEditModal from '../Modals/NoteEditModal';
import { debugLog } from '../../utils/debugLog';
import ConfirmDialog from '../Modals/ConfirmDialog';
import DatasheetModal from '../Modals/DatasheetModal';
import DataSourceModal from '../Modals/DataSourceModal';
import S2PUploadModal from '../Modals/S2PUploadModal';
import WelcomeModal from '../Onboarding/WelcomeModal';
import TutorialOverlay from '../Onboarding/TutorialOverlay';
import { COMPONENT_LIBRARY } from '../../data/componentLibrary';
import { createComponentNode, cloneComponentNode, remapEdges } from '../../utils/componentNodeFactory';

// Temporary simple test node
const TestNode = ({ data }) => (
  <div style={{
    padding: '20px',
    background: 'red',
    border: '2px solid white',
    borderRadius: '8px',
    color: 'white',
    fontWeight: 'bold'
  }}>
    TEST: {data.name}
  </div>
);

const baseNodeTypes = {
  inputSource: RFNode,
  noiseSource: RFNode,
  lna: RFNode,
  powerAmplifier: RFNode,
  vga: RFNode,
  gainBlock: RFNode,
  driver: RFNode,
  attenuator: RFNode,
  variableAttenuator: RFNode,
  cable: RFNode,
  splitter: RFNode,
  combiner: RFNode,
  bandpassFilter: RFNode,
  lowpassFilter: RFNode,
  highpassFilter: RFNode,
  bandstopFilter: RFNode,
  passiveMixer: RFNode,
  activeMixer: RFNode,
  spdtSwitch: RFNode,
  sp4tSwitch: RFNode,
  termination: RFNode,
  generic: RFNode,
  measurement: MeasurementNode,
  note: NoteNode
};

const MainLayout = () => {
  const {
    nodes,
    edges,
    setNodes,
    setNodesWithoutModifying,
    setEdges,
    addNode,
    setSelectedNodeId,
    updateNodeData,
    isPanelCollapsed,
    showWelcomeModal,
    showTutorial,
    tutorialStep,
    tutorialActions,
    completeTutorialAction,
    checkFirstTimeUser,
    startTutorial,
    skipTutorial,
    pauseTutorial,
    nextTutorialStep,
    previousTutorialStep,
    completeTutorial,
    hasUnsavedChanges,
    isInspectorOpen,
    isInspectorPinned,
    openInspector,
    closeInspector,
    toggleInspector,
    openNoteModal,
    isComponentDetailsPanelOpen,
    toggleComponentDetailsPanel,
    componentLibrary,
    catalogComponents,
    loadComponentLibrary,
    chainMode,
    toggleChainMode,
    handleDataSourceChange,
    ENABLE_CANVAS_LOAD_TRANSITION,
    isCanvasLoading,
    setCanvasLoading
  } = useStore();

  // Zundo temporal store for undo/redo
  const { undo, redo, clear: clearHistory } = temporalStore.getState();
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // Subscribe to temporal store changes for undo/redo state
  useEffect(() => {
    const unsubscribe = temporalStore.subscribe((state) => {
      setCanUndo(state.pastStates.length > 0);
      setCanRedo(state.futureStates.length > 0);
    });
    return unsubscribe;
  }, []);

  const reactFlowInstance = useReactFlow();
  const { fitView } = reactFlowInstance;

  // Expose React Flow instance globally for PDF generation
  React.useEffect(() => {
    window.reactFlowInstance = reactFlowInstance;
    return () => {
      delete window.reactFlowInstance;
    };
  }, [reactFlowInstance]);

  const copiedNodeRef = useRef(null);
  const lastPastePositionRef = useRef(null);
  const isDraggingNodeRef = useRef(false); // Track when user is actively dragging a node
  const [viewport, setViewport] = useState({ x: 0, y: 0, zoom: 1.5 });
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(false);
  const [isPlotPanelOpen, setIsPlotPanelOpen] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState(null);
  const [contextMenuClickPosition, setContextMenuClickPosition] = useState(null);
  const [edgeContextMenu, setEdgeContextMenu] = useState(null); // { position: {x, y}, edgeId: string, edge: object }
  const [nodeContextMenu, setNodeContextMenu] = useState(null); // { position: {x, y}, node: object }
  const [dataSourceModalNode, setDataSourceModalNode] = useState(null); // Node for data source modal
  const [s2pUploadNode, setS2pUploadNode] = useState(null); // Node for S2P upload modal
  const [insertionMode, setInsertionMode] = useState(null); // { edge: object } - tracks edge being split for component insertion

  // Grid snapping constants and helper function
  const GRID_SIZE = 15;
  const snapToGrid = useCallback((position) => ({
    x: Math.round(position.x / GRID_SIZE) * GRID_SIZE,
    y: Math.round(position.y / GRID_SIZE) * GRID_SIZE
  }), []);

  // Build dynamic nodeTypes that include catalog components
  const nodeTypes = React.useMemo(() => {
    const dynamicTypes = { ...baseNodeTypes };

    // Register catalog components
    if (catalogComponents && catalogComponents.length > 0) {
      catalogComponents.forEach(comp => {
        dynamicTypes[comp.type] = RFNode;
      });
    }

    return dynamicTypes;
  }, [catalogComponents]);

  // Load component library on mount
  useEffect(() => {
    loadComponentLibrary();
  }, [loadComponentLibrary]);

  // Check if this is a first-time user or if tutorial is forced via URL parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('tutorial') === 'true' || urlParams.get('onboarding') === 'true') {
      // Force tutorial by clearing localStorage and showing welcome modal
      localStorage.removeItem(tutorialCompletedKey(useStore.getState().user.id));
      useStore.getState().setShowWelcomeModal(true);
    } else {
      // Normal first-time user check
      checkFirstTimeUser();
    }
  }, [checkFirstTimeUser]);

  // Warn user before closing window with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
        return ''; // Some browsers display this message
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Auto-fit view when a project is loaded
  useEffect(() => {
    const handleProjectLoaded = () => {
      // Use double requestAnimationFrame to ensure React Flow has fully rendered
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Get current nodes from store (not closure) to avoid stale data
          const currentNodes = useStore.getState().nodes;
          if (currentNodes.length > 0) {
            fitView({
              padding: 0.2,
              duration: 400,
              maxZoom: 1.5
            });
            // Hide loading overlay after fitView animation completes
            setTimeout(() => {
              useStore.getState().setCanvasLoading(false);
            }, 450);
          } else {
            // No nodes, clear loading state immediately
            useStore.getState().setCanvasLoading(false);
          }
        });
      });
    };

    window.addEventListener('project-loaded', handleProjectLoaded);

    return () => {
      window.removeEventListener('project-loaded', handleProjectLoaded);
    };
  }, [fitView]);

  // Safety timeout for canvas loading state
  useEffect(() => {
    if (isCanvasLoading) {
      const safety = setTimeout(() => {
        console.warn('Canvas loading timeout, forcing clear');
        setCanvasLoading(false);
      }, 5000);
      return () => clearTimeout(safety);
    }
  }, [isCanvasLoading, setCanvasLoading]);

  // Auto-fit view when a component is added in chain mode or project is loaded
  useEffect(() => {
    const handleFitViewRequested = () => {
      const fitViewButton = document.querySelector('.react-flow__controls-fitview');
      if (fitViewButton) {
        fitViewButton.click();
        // Clear loading overlay after layout settles
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            useStore.getState().setCanvasLoading(false);
          });
        });
      } else {
        // No button found, clear loading anyway
        useStore.getState().setCanvasLoading(false);
      }
    };

    window.addEventListener('fit-view-requested', handleFitViewRequested);

    return () => {
      window.removeEventListener('fit-view-requested', handleFitViewRequested);
    };
  }, []);

  // Open plot panel when sweep is run
  useEffect(() => {
    const handleOpenPlotPanel = () => {
      setIsPlotPanelOpen(true);
    };

    window.addEventListener('open-plot-panel', handleOpenPlotPanel);

    return () => {
      window.removeEventListener('open-plot-panel', handleOpenPlotPanel);
    };
  }, []);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger shortcuts if user is typing in an input/textarea
      const target = e.target;
      const isTyping = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

      // Undo: Ctrl+Z
      if (e.ctrlKey && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        const temporal = temporalStore.getState();
        const storeState = useStore.getState();
        const lastPastState = temporal.pastStates[temporal.pastStates.length - 1];
        const currentInputSource = storeState.nodes?.find(n => n.type === 'inputSource');
        const lastPastInputSource = lastPastState?.nodes?.find(n => n.type === 'inputSource');
        debugLog('UNDO_ATTEMPT', {
          pastStatesLength: temporal.pastStates.length,
          currentPower: currentInputSource?.data?.parameters?.power?.value,
          lastPastStatePower: lastPastInputSource?.data?.parameters?.power?.value
        });
        if (temporal.pastStates.length > 0) {
          temporal.undo();
          const afterInputSource = useStore.getState().nodes?.find(n => n.type === 'inputSource');
          debugLog('UNDO_EXECUTED', {
            powerAfterUndo: afterInputSource?.data?.parameters?.power?.value,
            futureStatesAfter: temporalStore.getState().futureStates.length
          });
          // Track undo for tutorial
          if (showTutorial) {
            completeTutorialAction('undoPerformed');
          }
        }
      }

      // Redo: Ctrl+Y or Ctrl+Shift+Z
      if ((e.ctrlKey && e.key === 'y') || (e.ctrlKey && e.shiftKey && e.key === 'z')) {
        e.preventDefault();
        if (canRedo) redo();
      }

      // Save: Ctrl+S
      if (e.ctrlKey && e.key === 's' && !e.shiftKey && !isTyping) {
        e.preventDefault();
        // Dispatch custom event that Header component will listen for
        window.dispatchEvent(new CustomEvent('save-project'));
      }

      // Save As: Ctrl+Shift+S
      if (e.ctrlKey && e.shiftKey && e.key === 'S' && !isTyping) {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('save-project-as'));
      }

      // New Project: Ctrl+Shift+N
      if (e.ctrlKey && e.shiftKey && e.key === 'N' && !isTyping) {
        e.preventDefault();
        e.stopImmediatePropagation();
        window.dispatchEvent(new CustomEvent('new-project'));
        return;
      }

      // Load Project: Ctrl+L
      if (e.ctrlKey && e.key === 'l' && !isTyping) {
        e.preventDefault();
        e.stopImmediatePropagation();
        window.dispatchEvent(new CustomEvent('load-project'));
        return;
      }

      // Create PDF: Ctrl+P
      if (e.ctrlKey && e.key === 'p' && !isTyping) {
        e.preventDefault();
        e.stopImmediatePropagation();
        window.dispatchEvent(new CustomEvent('create-pdf'));
        return;
      }

      // Copy: Ctrl+C
      if (e.ctrlKey && e.key === 'c' && !isTyping) {
        const selectedNodes = nodes.filter(n => n.selected);
        if (selectedNodes.length > 0) {
          e.preventDefault();
          copiedNodeRef.current = selectedNodes;
          lastPastePositionRef.current = null; // Reset paste position on new copy
          console.log('Copied nodes:', selectedNodes.map(n => n.id));
          // Track copy for tutorial
          if (showTutorial) {
            completeTutorialAction('nodeCopied');
          }
        }
      }

      // Paste: Ctrl+V
      if (e.ctrlKey && e.key === 'v' && !isTyping) {
        if (copiedNodeRef.current && copiedNodeRef.current.length > 0) {
          e.preventDefault();

          // Calculate offset for paste
          let offsetX, offsetY;
          if (lastPastePositionRef.current) {
            // Subsequent paste - use cascading Y offset only
            offsetX = 0;
            offsetY = lastPastePositionRef.current.y;
          } else {
            // First paste - calculate height of selected group from DOM
            offsetX = 0; // No X offset

            // Find the bounding box of all copied nodes by querying their actual DOM elements
            let minY = Infinity;
            let maxY = -Infinity;

            copiedNodeRef.current.forEach(copiedNode => {
              const nodeElement = document.querySelector(`[data-id="${copiedNode.id}"]`);
              if (nodeElement) {
                const rect = nodeElement.getBoundingClientRect();
                const nodeHeight = rect.height / viewport.zoom;
                const nodeTop = copiedNode.position.y;
                const nodeBottom = nodeTop + nodeHeight;

                minY = Math.min(minY, nodeTop);
                maxY = Math.max(maxY, nodeBottom);
              } else {
                // Fallback if DOM element not found - estimate 150px height
                const nodeTop = copiedNode.position.y;
                const nodeBottom = nodeTop + 150;
                minY = Math.min(minY, nodeTop);
                maxY = Math.max(maxY, nodeBottom);
              }
            });

            // Calculate total height of selection and add padding
            const selectionHeight = maxY - minY;
            offsetY = selectionHeight + 50; // Selection height + 50px padding
          }

          // Create new nodes for all copied nodes using factory (ensures proper catalog component initialization)
          const oldIdToNewId = {}; // Map old IDs to new IDs for edge reconnection

          const newNodes = copiedNodeRef.current.map((copiedNode) => {
            // Use factory to properly clone node (handles catalog component initialization)
            const clonedNode = cloneComponentNode(copiedNode, {
              position: snapToGrid({
                x: copiedNode.position.x + offsetX,
                y: copiedNode.position.y + offsetY
              })
            });

            oldIdToNewId[copiedNode.id] = clonedNode.id;
            clonedNode.selected = true; // Auto-select pasted nodes

            return clonedNode;
          });

          // Copy edges that connect copied nodes to each other using factory helper
          const copiedNodeIds = new Set(copiedNodeRef.current.map(n => n.id));
          const edgesToCopy = edges.filter(edge =>
            copiedNodeIds.has(edge.source) && copiedNodeIds.has(edge.target)
          );

          const newEdges = remapEdges(edgesToCopy, new Map(Object.entries(oldIdToNewId)));

          // Update last paste position for cascading (Y only, cumulative)
          const currentOffsetY = lastPastePositionRef.current ? lastPastePositionRef.current.y : offsetY;
          lastPastePositionRef.current = {
            x: 0,
            y: currentOffsetY + 200 // Add 200px for each subsequent paste
          };

          // Deselect all existing nodes and add new nodes (which are selected)
          setNodes([...nodes.map(n => ({ ...n, selected: false })), ...newNodes]);
          setEdges([...edges, ...newEdges]);

          // Use requestAnimationFrame to ensure ReactFlow processes the new nodes before we trigger selection
          requestAnimationFrame(() => {
            // Re-apply selection to ensure ReactFlow's internal state is synchronized
            setNodes(currentNodes =>
              currentNodes.map(n => ({
                ...n,
                selected: newNodes.some(newNode => newNode.id === n.id)
              }))
            );
          });

          console.log('Pasted nodes:', newNodes.map(n => n.id));
          console.log('Pasted edges:', newEdges.map(e => e.id));
          // Track paste for tutorial
          if (showTutorial) {
            completeTutorialAction('nodePasted');
          }
        }
      }

      // Delete: Delete or Backspace (when node or edge selected and not typing in input)
      if ((e.key === 'Delete' || e.key === 'Backspace') && !isTyping) {
        const selectedNodes = nodes.filter(n => n.selected);
        const selectedEdges = edges.filter(e => e.selected);

        if (selectedNodes.length > 0) {
          e.preventDefault();
          handleDeleteNodes(selectedNodes.map(n => n.id));
        } else if (selectedEdges.length > 0) {
          e.preventDefault();
          setEdges(edges.filter(e => !e.selected));
        }
      }

      // Select All: Ctrl+A (only when not typing in input)
      if (e.ctrlKey && e.key === 'a' && !isTyping) {
        e.preventDefault();
        // Select all nodes
        setNodes(nodes.map(node => ({ ...node, selected: true })));
      }

      // Toggle Inspector: I key
      if (e.key.toLowerCase() === 'i' && !isTyping && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
        e.preventDefault();
        toggleInspector();
      }

      // Toggle RightPanel: R key
      if (e.key.toLowerCase() === 'r' && !isTyping && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
        e.preventDefault();
        setIsRightPanelOpen(prev => !prev);
      }

      // Toggle Component Details Panel: D key
      if (e.key.toLowerCase() === 'd' && !isTyping && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
        e.preventDefault();
        toggleComponentDetailsPanel();
      }

      // Toggle PlotPanel: P key (when shift is held)
      if (e.key.toLowerCase() === 'p' && e.shiftKey && !isTyping && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setIsPlotPanelOpen(prev => !prev);
      }

      // Component hotkeys (only when not typing, not in modal, and no modifier keys pressed)
      const { isNoteModalOpen } = useStore.getState();
      if (!isTyping && !isNoteModalOpen && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
        const key = e.key.toLowerCase();
        let componentType = null;

        switch (key) {
          case 's':
            componentType = 'inputSource'; // Signal Generator
            break;
          case 'p':
            componentType = 'powerAmplifier'; // Power Amplifier
            break;
          case 'a':
            componentType = 'attenuator'; // Attenuator
            break;
          case 'c':
            componentType = 'cable'; // Cable
            break;
          case 'C': // Shift+C for Chain Mode toggle
            e.preventDefault();
            toggleChainMode();
            return;
          case 'l':
            componentType = 'splitter'; // Splitter (L for "spLitter")
            break;
          case 'm':
            componentType = 'combiner'; // Combiner (M for "Merge")
            break;
          case 'n':
            componentType = 'note'; // Note
            break;
          default:
            break;
        }

        if (componentType) {
          e.preventDefault();
          handleAddComponentByHotkey(componentType);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [canUndo, canRedo, undo, redo, nodes, edges, setNodes, setEdges, viewport, toggleInspector, showTutorial, completeTutorialAction]);

  const onMove = useCallback((event, newViewport) => {
    setViewport(newViewport);
    // Track viewport change for tutorial
    if (showTutorial) {
      completeTutorialAction('viewportChanged');
    }
  }, [showTutorial, completeTutorialAction]);


  const onNodesChange = useCallback((changes) => {
    // Track drag lifecycle to distinguish user drags from viewport operations
    let userDragEnded = false;

    changes.forEach(change => {
      if (change.type === 'position') {
        if (change.dragging === true) {
          // User started or is actively dragging
          isDraggingNodeRef.current = true;
        } else if (change.dragging === false && isDraggingNodeRef.current) {
          // User just finished dragging (transition from true -> false)
          userDragEnded = true;
          isDraggingNodeRef.current = false;
        }
        // If dragging === false and isDraggingNodeRef.current === false,
        // this is a viewport operation (pan/zoom/fitView), not a user drag
      }
    });

    // Always use setNodesWithoutModifying - actual modifications are handled elsewhere:
    // - Drag end: via isDraggingNodeRef + markAsModified (line 446)
    // - Value edits: via updateNodeData which sets hasUnsavedChanges
    // - Add/delete: via addNode/setNodes which set hasUnsavedChanges
    // This prevents pan/zoom/fitView/dimensions changes from incorrectly marking as modified
    const nodesSetter = setNodesWithoutModifying;

    // Use functional update to get latest nodes state
    nodesSetter((currentNodes) => {
      let updatedNodes = [...currentNodes];

      changes.forEach(change => {
        if (change.type === 'select') {
          updatedNodes = updatedNodes.map(node =>
            node.id === change.id ? { ...node, selected: change.selected } : node
          );
          if (change.selected) {
            setSelectedNodeId(change.id);
            // Auto-show inspector when node selected (unless pinned or user explicitly closed it)
            const { isInspectorPinned, isInspectorOpen } = useStore.getState();
            if (!isInspectorPinned && !isInspectorOpen) {
              openInspector();
            }
          } else {
            // Node deselected - check if any nodes are still selected
            const hasSelection = updatedNodes.some(n => n.id !== change.id && n.selected);
            if (!hasSelection) {
              setSelectedNodeId(null);
              // Auto-hide inspector when nothing selected (unless pinned)
              const { isInspectorPinned } = useStore.getState();
              if (!isInspectorPinned) {
                closeInspector();
              }
            }
          }
        } else if (change.type === 'position' && change.position) {
          updatedNodes = updatedNodes.map(node =>
            node.id === change.id ? { ...node, position: change.position } : node
          );
        } else if (change.type === 'remove') {
          updatedNodes = updatedNodes.filter(node => node.id !== change.id);
        }
      });

      return updatedNodes;
    });

    // Only mark as modified when user actually finishes dragging a node
    if (userDragEnded) {
      useStore.getState().markAsModified();
      // Track node move for tutorial
      if (showTutorial) {
        completeTutorialAction('nodeMoved');
      }
    }
  }, [setNodes, setNodesWithoutModifying, setSelectedNodeId, openInspector, closeInspector, showTutorial, completeTutorialAction]);

  const onEdgesChange = useCallback((changes) => {
    console.log('Edge changes:', changes);

    // Use ReactFlow's applyEdgeChanges helper to handle all change types properly
    const updatedEdges = applyEdgeChanges(changes, edges);

    setEdges(updatedEdges);
  }, [edges, setEdges]);

  const onConnect = useCallback((connection) => {
    const newEdge = {
      ...connection,
      id: `edge-${Date.now()}`,
      type: 'default',
      animated: false,
      deletable: true,
      reconnectable: true,
      selectable: true,
      focusable: true
    };

    setEdges([...edges, newEdge]);
  }, [edges, setEdges]);

  // Validate connections: prevent invalid connections like input-to-input or output-to-output
  const isValidConnection = useCallback((connection) => {
    // Connection must have source and target
    if (!connection.source || !connection.target) return false;

    // Prevent self-connections
    if (connection.source === connection.target) return false;

    // Prevent duplicate connections
    const isDuplicate = edges.some(edge =>
      edge.source === connection.source &&
      edge.target === connection.target &&
      edge.sourceHandle === connection.sourceHandle &&
      edge.targetHandle === connection.targetHandle
    );

    return !isDuplicate;
  }, [edges]);

  // Handle edge reconnection: when user drags an existing connection to a new node
  const onReconnect = useCallback((oldEdge, newConnection) => {
    // Validate the new connection
    if (!isValidConnection(newConnection)) {
      console.log('Invalid reconnection attempt');
      return;
    }

    // Remove the old edge and create the new one with updated source/target
    const updatedEdges = edges.filter(edge => edge.id !== oldEdge.id);
    const reconnectedEdge = {
      ...oldEdge,
      ...newConnection,
      id: `edge-${Date.now()}`,
      reconnectable: true,
      style: { stroke: THEME.connectionLine, strokeWidth: 2 }
    };

    setEdges([...updatedEdges, reconnectedEdge]);
    console.log('Edge reconnected from', oldEdge, 'to', newConnection);
  }, [edges, setEdges, isValidConnection]);

  // Handle drag-to-delete: when user drags edge handle to empty space
  const onConnectEnd = useCallback((event, connectionState) => {
    // connectionState contains info about the connection attempt
    // If user was dragging FROM an existing edge (reconnecting/deleting)
    if (connectionState.fromHandle && connectionState.fromHandle.type === 'source') {
      // Check if the drag ended over empty space (not over a target node)
      const targetIsPane = event.target?.classList.contains('react-flow__pane');

      if (targetIsPane) {
        // User dragged the edge to nowhere - find and delete edges from this source handle
        const sourceNodeId = connectionState.fromNode.id;
        const sourceHandleId = connectionState.fromHandle.id;

        // Remove edges that match this source
        const updatedEdges = edges.filter(edge => {
          const matchesSource = edge.source === sourceNodeId &&
                               (sourceHandleId ? edge.sourceHandle === sourceHandleId : true);
          return !matchesSource;
        });

        if (updatedEdges.length < edges.length) {
          setEdges(updatedEdges);
          console.log('Edge deleted by dragging to empty space');
        }
      }
    }

    // Handle dragging from target handle (the end of an edge)
    if (connectionState.fromHandle && connectionState.fromHandle.type === 'target') {
      const targetIsPane = event.target?.classList.contains('react-flow__pane');

      if (targetIsPane) {
        const targetNodeId = connectionState.fromNode.id;
        const targetHandleId = connectionState.fromHandle.id;

        // Remove edges that connect to this target
        const updatedEdges = edges.filter(edge => {
          const matchesTarget = edge.target === targetNodeId &&
                               (targetHandleId ? edge.targetHandle === targetHandleId : true);
          return !matchesTarget;
        });

        if (updatedEdges.length < edges.length) {
          setEdges(updatedEdges);
          console.log('Edge deleted by dragging to empty space');
        }
      }
    }
  }, [edges, setEdges]);

  const handleDeleteNode = useCallback((nodeId) => {
    const nodeToDelete = nodes.find(n => n.id === nodeId);
    if (!nodeToDelete) return;

    // Manual Mode (chainMode OFF) OR Note component: Simple deletion, no auto-reconnect or shifting
    // Notes always use simple deletion regardless of chain mode
    if (!chainMode || nodeToDelete.type === 'note') {
      const updatedNodes = nodes.filter(n => n.id !== nodeId);
      const updatedEdges = edges.filter(e => e.source !== nodeId && e.target !== nodeId);
      setNodes(updatedNodes);
      setEdges(updatedEdges);
      console.log('Node deleted in manual mode:', nodeId);
      return;
    }

    // Chain Mode (ON): Smart deletion with reconnection and shifting
    // Find all edges connected to this node
    const inputEdges = edges.filter(e => e.target === nodeId);
    const outputEdges = edges.filter(e => e.source === nodeId);

    // Get the upstream (source) and downstream (target) nodes
    const upstreamNode = inputEdges.length > 0
      ? nodes.find(n => n.id === inputEdges[0].source)
      : null;
    const downstreamNode = outputEdges.length > 0
      ? nodes.find(n => n.id === outputEdges[0].target)
      : null;

    const inputEdge = inputEdges[0] || null;
    const outputEdge = outputEdges[0] || null;

    console.log('DEBUG: Analyzing deletion for reconnection:', {
      nodeId,
      inputEdges: inputEdges.length,
      outputEdges: outputEdges.length,
      upstreamNode: upstreamNode?.id,
      downstreamNode: downstreamNode?.id,
      inputEdge: inputEdge ? `${inputEdge.source}->${inputEdge.target}` : null,
      outputEdge: outputEdge ? `${outputEdge.source}->${outputEdge.target}` : null
    });

    // Determine if we should reconnect (both upstream and downstream exist)
    const shouldReconnect = upstreamNode !== null && downstreamNode !== null;
    let reconnectionEdge = null;

    // If we should reconnect, prepare the new edge
    if (shouldReconnect) {
      console.log('DEBUG: Should reconnect is TRUE, creating edge...');

      if (upstreamNode && downstreamNode && inputEdge && outputEdge) {
        reconnectionEdge = {
          id: `edge-${Date.now()}`,
          source: upstreamNode.id,
          sourceHandle: inputEdge.sourceHandle,
          target: downstreamNode.id,
          targetHandle: outputEdge.targetHandle,
          type: 'default',
          animated: false,
          deletable: true,
          style: { stroke: THEME.connectionLine, strokeWidth: 2 }
        };
        console.log('DEBUG: Reconnection edge created:', reconnectionEdge);
      } else {
        console.log('DEBUG: Failed inner check - missing required data');
      }
    } else {
      console.log('DEBUG: Should reconnect is FALSE');
    }

    // Calculate shift amount
    const estimatedNodeWidth = 190;
    const nodeSpacing = 30;
    const shiftAmount = estimatedNodeWidth + nodeSpacing;

    // Find all nodes downstream (to the right) of the deleted node
    const nodesToShift = nodes.filter(n =>
      n.id !== nodeId && n.position.x > nodeToDelete.position.x
    );

    // Remove the node and its connected edges
    let updatedEdges = edges.filter(e => e.source !== nodeId && e.target !== nodeId);
    console.log('DEBUG: Edges after filtering out deleted node:', updatedEdges.length);

    // Add reconnection edge if needed
    if (reconnectionEdge) {
      updatedEdges.push(reconnectionEdge);
      console.log('DEBUG: Added reconnection edge to updatedEdges, total edges:', updatedEdges.length);
    } else {
      console.log('DEBUG: No reconnection edge to add');
    }

    // Shift downstream nodes left
    const updatedNodes = nodes
      .filter(n => n.id !== nodeId)
      .map(node => {
        if (nodesToShift.some(n => n.id === node.id)) {
          return {
            ...node,
            position: {
              x: node.position.x - shiftAmount,
              y: node.position.y
            }
          };
        }
        return node;
      });

    // Update state atomically
    setNodes(updatedNodes);
    setEdges(updatedEdges);

    console.log('Node deleted with reconnection:', {
      deleted: nodeId,
      reconnected: shouldReconnect,
      upstreamNode: upstreamNode?.id || 'none',
      downstreamNode: downstreamNode?.id || 'none',
      inputEdge: inputEdge ? `${inputEdge.source} -> ${inputEdge.target}` : 'none',
      outputEdge: outputEdge ? `${outputEdge.source} -> ${outputEdge.target}` : 'none',
      reconnectionEdge: reconnectionEdge ? `${reconnectionEdge.source} -> ${reconnectionEdge.target}` : 'none',
      nodesShifted: nodesToShift.length
    });
  }, [nodes, edges, setNodes, setEdges, chainMode]);

  const handleDeleteNodes = useCallback((nodeIds) => {
    if (nodeIds.length === 0) return;

    // If only one node is being deleted, use the single-node deletion logic with reconnection
    if (nodeIds.length === 1) {
      console.log('DEBUG: Single node delete via handleDeleteNodes, delegating to handleDeleteNode');
      handleDeleteNode(nodeIds[0]);
      return;
    }

    const nodeIdSet = new Set(nodeIds);
    const nodesToDelete = nodes.filter(n => nodeIdSet.has(n.id));

    // For multi-delete, we don't reconnect, but we do shift left
    // Find the leftmost deleted node position
    const leftmostDeletedX = Math.min(...nodesToDelete.map(n => n.position.x));
    const rightmostDeletedX = Math.max(...nodesToDelete.map(n => n.position.x));

    // Calculate shift amount (approximate based on typical node width)
    const estimatedNodeWidth = 190;
    const nodeSpacing = 30;
    const shiftAmount = (estimatedNodeWidth + nodeSpacing) * nodeIds.length;

    // Find nodes downstream (to the right) of the rightmost deleted node
    const nodesToShift = nodes.filter(n =>
      !nodeIdSet.has(n.id) && n.position.x > rightmostDeletedX
    );

    // Remove deleted nodes and their connected edges
    const updatedNodes = nodes
      .filter(n => !nodeIdSet.has(n.id))
      .map(node => {
        if (nodesToShift.some(n => n.id === node.id)) {
          return {
            ...node,
            position: {
              x: node.position.x - shiftAmount,
              y: node.position.y
            }
          };
        }
        return node;
      });

    const updatedEdges = edges.filter(e =>
      !nodeIdSet.has(e.source) && !nodeIdSet.has(e.target)
    );

    // Update state atomically
    setNodes(updatedNodes);
    setEdges(updatedEdges);

    console.log('Multiple nodes deleted:', {
      deleted: nodeIds.length,
      nodesShifted: nodesToShift.length
    });
  }, [nodes, edges, setNodes, setEdges, handleDeleteNode]);

  // Handle cloning a node from context menu
  const handleCloneNode = useCallback((nodeId) => {
    const nodeToCopy = nodes.find(n => n.id === nodeId);
    if (!nodeToCopy) return;

    // Use factory to properly clone node (ensures catalog component initialization)
    const newNode = cloneComponentNode(nodeToCopy, {
      offsetX: 50,
      offsetY: 50
    });
    newNode.selected = true;

    // Deselect all nodes and add the new cloned node
    const updatedNodes = nodes.map(n => ({ ...n, selected: false }));
    updatedNodes.push(newNode);

    setNodes(updatedNodes);

    console.log('Node cloned:', {
      original: nodeId,
      clone: newNode.id
    });
  }, [nodes, setNodes]);

  // Handle setting alias for a node from context menu
  const handleSetNodeAlias = useCallback((nodeId) => {
    // Trigger edit mode by setting a temporary flag in node data
    updateNodeData(nodeId, { _triggerAliasEdit: Date.now() });
  }, [updateNodeData]);

  // Handle exporting sweep data to CSV from measurement node context menu
  const handleExportSweepCSV = useCallback(() => {
    const sweepResults = useStore.getState().sweepResults;
    if (!sweepResults || !sweepResults.frequencies || sweepResults.frequencies.length === 0) {
      return;
    }

    // Build CSV content: Freq (MHz), Gain (dB), NF (dB), OP1dB (dBm), OIP3 (dBm)
    const headers = ['Freq (MHz)', 'Gain (dB)', 'NF (dB)', 'OP1dB (dBm)', 'OIP3 (dBm)'];
    const rows = [headers.join(',')];

    for (let i = 0; i < sweepResults.frequencies.length; i++) {
      const freq = sweepResults.frequencies[i];
      const gain = sweepResults.gain?.[i];
      const nf = sweepResults.noiseFigure?.[i];
      const p1db = sweepResults.p1db?.[i];
      const oip3 = sweepResults.oip3?.[i];

      // Only include rows where freq has valid data (at least one non-null value)
      if (gain !== null || nf !== null || p1db !== null || oip3 !== null) {
        rows.push([
          freq?.toFixed(2) ?? '',
          gain?.toFixed(2) ?? '',
          nf?.toFixed(2) ?? '',
          p1db?.toFixed(2) ?? '',
          oip3?.toFixed(2) ?? ''
        ].join(','));
      }
    }

    // Create and download the file
    const csvContent = rows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const projectName = useStore.getState().currentProject?.name || 'untitled';
    const safeProjectName = projectName.replace(/[^a-zA-Z0-9_-]/g, '_');
    link.download = `${safeProjectName}_sweep_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  // Handle applying data source changes from modal
  const handleApplyDataSources = useCallback(async (selectedSources) => {
    if (!dataSourceModalNode) return;

    const nodeId = dataSourceModalNode.id;

    // Apply each data source change
    for (const [paramKey, sourceValue] of Object.entries(selectedSources)) {
      await handleDataSourceChange(nodeId, paramKey, sourceValue);
    }

    setDataSourceModalNode(null);
  }, [dataSourceModalNode, handleDataSourceChange]);

  const handleAddComponentByHotkey = (componentType) => {
    const componentDef = COMPONENT_LIBRARY[componentType];
    if (!componentDef) return;

    // Calculate position based on component type
    let position;

    // Notes use top-left cascade positioning (same as manual mode in LeftPanel)
    if (componentType === 'note') {
      // Calculate 50px from top-left of viewport in flow coordinates
      const startX = (-viewport.x + 50) / viewport.zoom;
      const startY = (-viewport.y + 50) / viewport.zoom;

      // Start at top-left, then offset until we find an unoccupied spot
      const cascadeStep = 30;
      let offsetMultiplier = 0;

      while (offsetMultiplier < 50) { // Safety limit
        const candidateX = startX + (offsetMultiplier * cascadeStep);
        const candidateY = startY + (offsetMultiplier * cascadeStep);

        const isOccupied = nodes.some(node => {
          const dx = Math.abs(node.position.x - candidateX);
          const dy = Math.abs(node.position.y - candidateY);
          return dx < 20 && dy < 20;
        });

        if (!isOccupied) {
          position = snapToGrid({ x: candidateX, y: candidateY });
          break;
        }
        offsetMultiplier++;
      }

      // Fallback if all positions occupied
      if (!position) {
        position = snapToGrid({
          x: startX + (offsetMultiplier * cascadeStep),
          y: startY + (offsetMultiplier * cascadeStep)
        });
      }

    } else {
      // Regular component positioning (to the right of rightmost node)
      if (nodes.length === 0) {
        position = snapToGrid({ x: 250, y: 150 });
      } else {
        const rightmostNode = nodes.reduce((rightmost, node) =>
          node.position.x > rightmost.position.x ? node : rightmost
        );

        // Get node width from DOM if possible
        const rightmostNodeElement = document.querySelector(`[data-id="${rightmostNode.id}"]`);
        let nodeWidth = 220;
        if (rightmostNodeElement) {
          const rect = rightmostNodeElement.getBoundingClientRect();
          nodeWidth = rect.width / viewport.zoom;
        }

        position = snapToGrid({
          x: rightmostNode.position.x + nodeWidth + 55,
          y: rightmostNode.position.y
        });
      }
    }

    // Use factory to create node (ensures proper catalog component initialization)
    const newNode = createComponentNode(componentDef, {
      position,
      nodeType: componentType
    });

    if (!newNode) {
      console.error('Failed to create node for component type:', componentType);
      return;
    }

    // Use addNode to trigger async initialization for catalog components
    addNode(newNode);

    // Auto-connect if possible
    if (nodes.length > 0 && componentDef.inputs && componentDef.inputs.length > 0) {
      const rightmostNode = nodes.reduce((rightmost, node) =>
        node.position.x > rightmost.position.x ? node : rightmost
      );

      const rightmostNodeDef = COMPONENT_LIBRARY[rightmostNode.type];

      if (rightmostNodeDef?.outputs && rightmostNodeDef.outputs.length > 0) {
        const usedOutputs = edges
          .filter(edge => edge.source === rightmostNode.id)
          .map(edge => edge.sourceHandle);

        const unusedOutput = rightmostNodeDef.outputs.find(output => !usedOutputs.includes(output));

        if (unusedOutput) {
          const newEdge = {
            id: `edge-${Date.now()}`,
            source: rightmostNode.id,
            sourceHandle: unusedOutput,
            target: newNode.id,
            targetHandle: componentDef.inputs[0],
            type: 'default',
            animated: false,
            style: { stroke: THEME.connectionLine, strokeWidth: 2 },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: THEME.connectionLine,
              width: 20,
              height: 20
            }
          };

          setEdges([...edges, newEdge]);
        }
      }
    }

    // Check if new node is outside viewport and auto-zoom if needed
    // Use double requestAnimationFrame to ensure React and ReactFlow have completed rendering
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Get actual canvas dimensions accounting for left panel (250px) only
        const canvasWidth = window.innerWidth - 250; // LeftPanel (250px)
        const canvasHeight = window.innerHeight - 60; // Account for header

        // Query the newly added node from DOM to get actual dimensions
        const newNodeElement = document.querySelector(`[data-id="${newNode.id}"]`);
        let actualNodeWidth = 220; // Fallback to max-width

        if (newNodeElement) {
          const rect = newNodeElement.getBoundingClientRect();
          actualNodeWidth = rect.width / viewport.zoom;
        }

        // Calculate visible boundaries in flow coordinates (correct formula)
        const viewportLeft = -viewport.x / viewport.zoom;
        const viewportRight = viewportLeft + (canvasWidth / viewport.zoom);
        const viewportTop = -viewport.y / viewport.zoom;
        const viewportBottom = viewportTop + (canvasHeight / viewport.zoom);

        const nodeRight = newNode.position.x + actualNodeWidth;

        // Trigger zoom at 90% of viewport width for better UX (adds buffer)
        if (nodeRight > viewportRight * 0.9 || newNode.position.y > viewportBottom * 0.9) {
          fitView({
            padding: 0.2,
            duration: 400,
            maxZoom: 1.5
          });
        }
      });
    });
  };

  const onNodeDoubleClick = (event, node) => {
    // Check if it's a note node - open NoteEditModal
    if (node.type === 'note') {
      openNoteModal(node.id);
    } else {
      // For all other nodes - open Component Details Panel
      useStore.getState().openComponentDetailsPanel();
    }
  };

  const onPaneClick = (event) => {
    console.log('Pane clicked', event, 'detail:', event.detail);

    // Detect double-click using event.detail (standard DOM API)
    // event.detail === 1 for single click, 2 for double-click
    if (event.detail === 2) {
      console.log('Pane double clicked!', event);
      // Get click position relative to the viewport
      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      // Store both screen position (for menu) and flow position (for adding component)
      setContextMenuPosition({ x: event.clientX, y: event.clientY });
      setContextMenuClickPosition({ x, y });
    }
  };

  // Handle edge click for selection
  const onEdgeClick = useCallback((event, edge) => {
    console.log('Edge clicked:', edge.id);
    // Edge selection is handled automatically by ReactFlow through onEdgesChange
    // This handler is just for debugging and potential custom logic
  }, []);

  // Handle right-click on edge
  const onEdgeContextMenu = useCallback((event, edge) => {
    event.preventDefault();
    setEdgeContextMenu({
      position: { x: event.clientX, y: event.clientY },
      edgeId: edge.id,
      edge: edge // Store full edge object for insertion
    });
  }, []);

  // Handle right-click on node
  const onNodeContextMenu = useCallback((event, node) => {
    event.preventDefault();
    setNodeContextMenu({
      position: { x: event.clientX, y: event.clientY },
      node: node
    });
    // Track context menu for tutorial
    if (showTutorial) {
      completeTutorialAction('contextMenuOpened');
    }
  }, [showTutorial, completeTutorialAction]);

  // Handle edge deletion from context menu
  const handleDeleteEdge = useCallback(() => {
    if (!edgeContextMenu) return;

    const updatedEdges = edges.filter(e => e.id !== edgeContextMenu.edgeId);
    setEdges(updatedEdges);
    console.log('Edge deleted via context menu:', edgeContextMenu.edgeId);
  }, [edgeContextMenu, edges, setEdges]);

  // Handle insert component from edge context menu
  const handleInsertComponent = useCallback(() => {
    if (!edgeContextMenu || !edgeContextMenu.edge) return;

    const edge = edgeContextMenu.edge;
    const sourceNode = nodes.find(n => n.id === edge.source);
    const targetNode = nodes.find(n => n.id === edge.target);

    if (!sourceNode || !targetNode) {
      console.error('Could not find source or target node for edge insertion');
      return;
    }

    // Calculate midpoint position between source and target nodes
    const midX = (sourceNode.position.x + targetNode.position.x) / 2;
    const midY = (sourceNode.position.y + targetNode.position.y) / 2;

    // Convert flow coordinates to screen coordinates for menu positioning
    const { x, y, zoom } = viewport;
    const screenX = midX * zoom + x;
    const screenY = midY * zoom + y;

    // Store insertion mode with edge details and set up component menu
    setInsertionMode({ edge: edge });
    setContextMenuPosition({ x: edgeContextMenu.position.x, y: edgeContextMenu.position.y });
    setContextMenuClickPosition({ x: screenX, y: screenY });

    console.log('Insertion mode activated for edge:', edge.id);
  }, [edgeContextMenu, nodes, viewport]);

  // Handle adding component between two connected nodes
  const handleAddComponentBetween = useCallback((componentType) => {
    if (!insertionMode || !insertionMode.edge) {
      console.error('Cannot add component between nodes: no insertion mode data');
      return;
    }

    const edge = insertionMode.edge;
    const componentDef = componentLibrary[componentType] || COMPONENT_LIBRARY[componentType];

    if (!componentDef) {
      console.error('No component definition found for:', componentType);
      return;
    }

    const sourceNode = nodes.find(n => n.id === edge.source);
    const targetNode = nodes.find(n => n.id === edge.target);

    if (!sourceNode || !targetNode) {
      console.error('Could not find source or target node for insertion');
      return;
    }

    // Estimate component width and spacing (typical values from the app)
    const estimatedNodeWidth = 190; // Typical node width
    const nodeSpacing = 30; // Spacing between nodes
    const shiftAmount = estimatedNodeWidth + nodeSpacing;

    // Position new component right after the source node
    const newX = sourceNode.position.x + estimatedNodeWidth + nodeSpacing;
    const newY = sourceNode.position.y; // Start with same Y as source, will adjust for handle alignment

    // Create new node using factory (ensures proper catalog component initialization)
    const newNode = createComponentNode(componentDef, {
      position: snapToGrid({ x: newX, y: newY }),
      nodeType: componentType
    });

    if (!newNode) {
      console.error('Failed to create node for insert between:', componentType);
      return;
    }

    // Find all nodes that are downstream (to the right) of the insertion point
    // These need to be shifted right to make space (only in Chain Mode)
    const nodesToShift = chainMode
      ? nodes.filter(n => n.position.x >= newX && n.id !== sourceNode.id)
      : [];

    // Shift downstream nodes to the right (only in Chain Mode)
    const updatedNodes = nodes.map(node => {
      if (nodesToShift.some(n => n.id === node.id)) {
        return {
          ...node,
          position: {
            x: node.position.x + shiftAmount,
            y: node.position.y
          }
        };
      }
      return node;
    });

    // Add the new node to the updated nodes list
    updatedNodes.push(newNode);

    // Create two new edges: source -> new component -> target
    const edgeToNew = {
      id: `edge-${Date.now()}-1`,
      source: edge.source,
      sourceHandle: edge.sourceHandle,
      target: newNode.id,
      targetHandle: componentDef.inputs && componentDef.inputs[0] ? componentDef.inputs[0] : 'input',
      type: 'default',
      animated: false,
      deletable: true,
      style: { stroke: THEME.connectionLine, strokeWidth: 2 }
    };

    const edgeFromNew = {
      id: `edge-${Date.now()}-2`,
      source: newNode.id,
      sourceHandle: componentDef.outputs && componentDef.outputs[0] ? componentDef.outputs[0] : 'output',
      target: edge.target,
      targetHandle: edge.targetHandle,
      type: 'default',
      animated: false,
      deletable: true,
      style: { stroke: THEME.connectionLine, strokeWidth: 2 }
    };

    // Update state: remove old edge, add new edges, update all nodes
    const updatedEdges = edges.filter(e => e.id !== edge.id);
    setEdges([...updatedEdges, edgeToNew, edgeFromNew]);
    setNodes(updatedNodes);

    // Clear insertion mode and context menu
    setInsertionMode(null);
    setContextMenuPosition(null);
    setContextMenuClickPosition(null);

    console.log('Component inserted between nodes:', {
      source: edge.source,
      newComponent: newNode.id,
      target: edge.target,
      nodesShifted: nodesToShift.length
    });
  }, [insertionMode, nodes, edges, componentLibrary, setNodes, setEdges, chainMode]);

  const handleAddComponentFromMenu = (componentType) => {
    if (!contextMenuClickPosition) return;

    // Check if we're in insertion mode (inserting between existing components)
    if (insertionMode && insertionMode.edge) {
      handleAddComponentBetween(componentType);
      return;
    }

    // Normal mode: add component at click position
    const componentDef = componentLibrary[componentType] || COMPONENT_LIBRARY[componentType];
    if (!componentDef) {
      console.error('No component definition found for:', componentType);
      return;
    }

    // Convert screen coordinates to flow coordinates
    const { x, y, zoom } = viewport;
    const flowX = (contextMenuClickPosition.x - x) / zoom;
    const flowY = (contextMenuClickPosition.y - y) / zoom;

    // Create new node at click position using factory (ensures proper catalog component initialization)
    const newNode = createComponentNode(componentDef, {
      position: snapToGrid({ x: flowX, y: flowY }),
      nodeType: componentType
    });

    if (!newNode) {
      console.error('Failed to create node at position:', componentType);
      return;
    }

    // Use addNode to trigger async initialization for catalog components
    addNode(newNode);

    // Close context menu
    setContextMenuPosition(null);
    setContextMenuClickPosition(null);
  };

  const handleCloseContextMenu = () => {
    setContextMenuPosition(null);
    setContextMenuClickPosition(null);
    setInsertionMode(null); // Clear insertion mode when closing menu
  };

  return (
    <Container>
      <Header />

      <ContentArea>
        {!isPanelCollapsed.left && <LeftPanel />}

        <CanvasArea>
          {ENABLE_CANVAS_LOAD_TRANSITION && (
            <CanvasLoadingOverlay $visible={isCanvasLoading}>
              <LoadingSpinner />
            </CanvasLoadingOverlay>
          )}
          {/* <DebugOverlay>
            Nodes: {nodes.length} | Edges: {edges.length}
            <br/>
            First node: {nodes.length > 0 ? `${nodes[0].id} at (${nodes[0].position.x}, ${nodes[0].position.y})` : 'none'}
          </DebugOverlay> */}
          <ReactFlowWrapper $loading={ENABLE_CANVAS_LOAD_TRANSITION && isCanvasLoading}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onReconnect={onReconnect}
              onConnectEnd={onConnectEnd}
              onNodeDoubleClick={onNodeDoubleClick}
              onEdgeClick={onEdgeClick}
              onEdgeContextMenu={onEdgeContextMenu}
              onNodeContextMenu={onNodeContextMenu}
              onPaneClick={onPaneClick}
              onMove={onMove}
              nodeTypes={nodeTypes}
              defaultEdgeOptions={{ deletable: true, reconnectable: true }}
              isValidConnection={isValidConnection}
              connectionMode="loose"
              connectionLineStyle={{ stroke: THEME.connectionLine, strokeWidth: 2 }}
              connectionLineType="default"
              viewport={viewport}
              attributionPosition="bottom-right"
              snapToGrid={true}
              snapGrid={[15, 15]}
              zoomOnScroll={true}
              zoomOnPinch={true}
              zoomOnDoubleClick={false}
              panOnScroll={false}
              panOnDrag={true}
              selectNodesOnDrag={true}
              selectionOnDrag={true}
              elementsSelectable={true}
              edgesFocusable={true}
              edgesReconnectable={true}
              panActivationKeyCode="Space"
              selectionKeyCode="Control"
              multiSelectionKeyCode="Control"
            >
              <Background
                variant="lines"
                color="#000000"
                gap={THEME.gridSpacing}
                lineWidth={1}
                style={{
                  strokeDasharray: '5 5'
                }}
              />
              <LogoWatermarkInCanvas src="/assets/GRF.webp" alt="GRF Logo" style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: '600px',
                opacity: 0.12,
                pointerEvents: 'none',
                zIndex: -1
              }} />
              <Controls
                showZoom={true}
                showFitView={true}
                showInteractive={false}
              />
            </ReactFlow>
          </ReactFlowWrapper>
        </CanvasArea>

        {isRightPanelOpen && <RightPanel />}
      </ContentArea>

      {/* <FloatingInspector /> */} {/* Removed - using Component Details Panel instead */}
      {/* <EditModal /> */} {/* Removed - using Component Details Panel instead */}
      <NoteEditModal />
      <ConfirmDialog />
      <DatasheetModal />
      <ComponentDetailsPanel />

      {/* Component Context Menu */}
      {contextMenuPosition && (
        <ComponentContextMenu
          position={contextMenuPosition}
          onClose={handleCloseContextMenu}
          onAddComponent={handleAddComponentFromMenu}
        />
      )}

      {/* Edge Context Menu */}
      {edgeContextMenu && (
        <EdgeContextMenu
          position={edgeContextMenu.position}
          onClose={() => setEdgeContextMenu(null)}
          onDelete={handleDeleteEdge}
          onInsert={handleInsertComponent}
        />
      )}

      {/* Node Context Menu */}
      {nodeContextMenu && (
        <NodeContextMenu
          position={nodeContextMenu.position}
          node={nodeContextMenu.node}
          onClose={() => setNodeContextMenu(null)}
          onDelete={() => handleDeleteNode(nodeContextMenu.node.id)}
          onClone={() => handleCloneNode(nodeContextMenu.node.id)}
          onAlias={() => handleSetNodeAlias(nodeContextMenu.node.id)}
          onChangeDataSources={() => setDataSourceModalNode(nodeContextMenu.node)}
          onUploadS2P={() => setS2pUploadNode(nodeContextMenu.node)}
          onExportSweepCSV={handleExportSweepCSV}
        />
      )}

      {/* Data Source Modal */}
      {dataSourceModalNode && (
        <DataSourceModal
          node={dataSourceModalNode}
          onClose={() => setDataSourceModalNode(null)}
          onApply={handleApplyDataSources}
        />
      )}

      {/* S2P Upload Modal */}
      {s2pUploadNode && (
        <S2PUploadModal
          isOpen={!!s2pUploadNode}
          onClose={() => setS2pUploadNode(null)}
          componentLibraryId={componentLibrary[s2pUploadNode.type]?.id}
          componentName={s2pUploadNode.data?.partNumber || s2pUploadNode.data?.name}
          onUploadSuccess={() => {
            loadComponentLibrary();
            setS2pUploadNode(null);
          }}
        />
      )}

      {/* Floating trigger button for Component Details - DISABLED, now redundant with component cards */}
      {/* {!isComponentDetailsPanelOpen && (
        <FloatingButton onClick={toggleComponentDetailsPanel} title="Component Details (D)">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
        </FloatingButton>
      )} */}

      {/* OPTION 3: Sliders/Properties icon - indicates editable settings */}
      {/* {!isComponentDetailsPanelOpen && (
        <FloatingButton onClick={toggleComponentDetailsPanel} title="Component Details (D)">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="4" y1="21" x2="4" y2="14"></line>
            <line x1="4" y1="10" x2="4" y2="3"></line>
            <line x1="12" y1="21" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12" y2="3"></line>
            <line x1="20" y1="21" x2="20" y2="16"></line>
            <line x1="20" y1="12" x2="20" y2="3"></line>
            <line x1="1" y1="14" x2="7" y2="14"></line>
            <line x1="9" y1="8" x2="15" y2="8"></line>
            <line x1="17" y1="16" x2="23" y2="16"></line>
          </svg>
        </FloatingButton>
      )} */}

      {/* Plot Panel */}
      {isPlotPanelOpen && <PlotPanel onClose={() => setIsPlotPanelOpen(false)} />}

      {/* Onboarding System */}
      {showWelcomeModal && (
        <WelcomeModal
          onStart={() => {
            // Clear the canvas for a fresh tutorial experience
            setNodes([]);
            setEdges([]);
            clearHistory();
            startTutorial();
          }}
          onSkip={skipTutorial}
        />
      )}

      {showTutorial && (
        <TutorialOverlay
          currentStep={tutorialStep}
          onNext={() => {
            // Close plot panel when advancing from step 21 (plot-panel step)
            if (tutorialStep === 21) {
              setIsPlotPanelOpen(false);
            }
            nextTutorialStep();
          }}
          onPrevious={previousTutorialStep}
          onSkip={skipTutorial}
          onComplete={completeTutorial}
          onPause={pauseTutorial}
          nodes={nodes}
          edges={edges}
          tutorialActions={tutorialActions}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100vw;
  height: 100vh;
  background: ${THEME.canvasBackground};
`;

const ContentArea = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  min-height: 0; /* Fix flex child height bug */
`;

const CanvasArea = styled.div`
  flex: 1;
  position: relative;
  background: ${THEME.canvasBackground};
  width: 100%;
  height: 100%;
`;

const CanvasLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${THEME.canvasBackground};
  z-index: 10;
  opacity: ${props => props.$visible ? 1 : 0};
  pointer-events: ${props => props.$visible ? 'auto' : 'none'};
  transition: opacity 200ms ease-out;
`;

const ReactFlowWrapper = styled.div`
  width: 100%;
  height: 100%;
  visibility: ${props => props.$loading ? 'hidden' : 'visible'};
  opacity: ${props => props.$loading ? 0 : 1};
  /* No transition when hiding (instant), fade-in when showing */
  transition: ${props => props.$loading ? 'none' : 'opacity 200ms ease-out, visibility 0ms'};
`;

const LoadingSpinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: rgba(255, 255, 255, 0.4);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const DebugOverlay = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 10000;
  background: rgba(255, 0, 0, 0.8);
  color: white;
  padding: 10px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 14px;
`;

const LogoWatermarkInCanvas = styled.img`
  user-select: none;
  filter: brightness(1.2);
`;

const FloatingButton = styled.button`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${THEME.buttonPrimary};
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all ${THEME.transitionFast};
  z-index: 900;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4), 0 3px 6px rgba(0, 0, 0, 0.3);
    background: ${THEME.buttonHover};
  }

  &:active {
    transform: scale(1.05);
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

export default MainLayout;
